import React, { KeyboardEvent } from 'react';
import ReactDOM from 'react-dom';
import { Component } from '../../../dispatcher/modules';
import { Keys } from '@progress/kendo-react-common';
import { isFirefox } from '../../../utility/Util';
import { PUZZLE_TAB_STRIP_PROPS } from './Types';
import * as _ from 'lodash';

export default class PuzzleTabStrip extends Component<PUZZLE_TAB_STRIP_PROPS, {}> {
    reference = React.createRef<HTMLDivElement>();
    public tabFocus = 0;

    componentDidMount(): void {
      this.setAccessibility();
    }

    /**
     * Attach click event to tab's so virtual cursor can trigger them.
     * Accessibility purposes
     */
    attachListeners = (ul: HTMLElement, tabCount: number = this.props.tabCount):void => {
      const { isTabPanel, selected, isDropDownActive } = this.props;
      let liItems = ul.getElementsByTagName('li');
      if (liItems.length) {
        // @ts-ignore
        [...liItems].forEach((li: HTMLLIElement, index) => {
          if (isTabPanel) {
            if (tabCount - 1 === index) {
              // drop down
              li.className = `k-item k-last ${isDropDownActive ? 'tp-drop-down-selector' : 'tp-drop-down-selector-hidden'}`;
            } else if (tabCount - 1 < index) {
              // invisible tabs
              li.className = 'k-item !k-invisible';
            } else {
              // visible tabs
              li.className = `k-item${tabCount - 2 === index ? ' k-last' : ''}${index === 0 ? ' k-first' : ''}${index === selected ? ' k-active' : ''}`;
              li.addEventListener('click', (e: MouseEvent) => {
                this.selectTab(e, li);
              });
            }
          } else {
            li.addEventListener('click', (e: MouseEvent) => {
              this.selectTab(e, li);
            });
          }
        });
      }
    };

    /**
     * Returns node(s) by passed string (role)
     *
     */
    getNodeByTagName = () => {
      const drawerNavigationEl: any = ReactDOM.findDOMNode(this);
      return Array.from(drawerNavigationEl.querySelectorAll('[role="tablist"]'));
    };

    /**
     * Updates dom to pass accessibility requirements
     */
    setAccessibility = (tabCount?:number) => {
      const tabList: any[] = this.getNodeByTagName();
      if (tabList.length) {
        tabList[0].setAttribute('aria-label', `${this.props.translate('SRL-navigation')}`);
        this.attachListeners(tabList[0], tabCount);
      }
    };

    /**
     * Changes tab on click
     * @param {MouseEvent} e
     * @param {any} li
     */
    selectTab = (e:MouseEvent, li: any):void => {
      const { selected, handleSelect, isTabPanel } = this.props;
      const { target } = e;
      let parentItem = li.parentElement;

      if ((!isTabPanel || (!target['classList']['value'].includes('tp-close-button') && !target['parentElement']['classList']['value'].includes('tp-close-button'))) // if isn't clicked on X button
        && parentItem?.getAttribute('role') === 'tablist') {
        const { children } = parentItem;
        let index = [...children].indexOf(li);
        if (index !== selected) {
          for (let item of children) {
            item.removeAttribute('tabindex');
          }

          this.tabFocus = index;
          li.setAttribute('tabindex', '-1');
          li.focus();

          handleSelect({ selected: index });
        }
      }
    };

    /**
     * Key up handler (changes focus only if event is sent from tab or tablist)
     * @param e
     * @param {boolean} isDropDown
     */
    switchTab = (e: any, isDropDown?:boolean): void => {
      if (['tab', 'tablist'].includes(e.target.getAttribute('role')) || isDropDown) {
        let { tabCount, handleSelect, isTabPanel, isDropDownActive, focusOnDownDown } = this.props;
        let isAllTabsDisabled = false;

        if (isTabPanel) {
          tabCount--;
        }
        if (isDropDown) {
          this.tabFocus = tabCount;
        }

        if (e.target.getAttribute('role') === 'tablist') {
          this.tabFocus = -1;
        }

        let liElements = e.target.children;

        if (e.target.getAttribute('role') === 'tab') {
          liElements = e.target.parentElement.children;
        } else if (isDropDown) {
          let focusedElement = _.get(e, 'target');
          while (_.get(focusedElement, 'nodeName').toLowerCase() !== 'ul') {
            focusedElement = _.get(focusedElement, 'parentElement');
          }
          liElements = focusedElement.children;
        }

        if ([Keys.right, Keys.left, Keys.up, Keys.down, Keys.home, Keys.end].includes(e.keyCode)) {
          switch (e.keyCode) {
            case Keys.right:
            case Keys.up:
              this.tabFocus++;
              if (isTabPanel && isDropDownActive && this.tabFocus === tabCount) {
                focusOnDownDown();
              } else {
                const { isAllDisabled, newTabFocus } = this.handleTabFocus(liElements, true);
                this.tabFocus = newTabFocus;
                isAllTabsDisabled = isAllDisabled;
              }
              break;
            case Keys.left:
            case Keys.down:
              this.tabFocus--;
              if (this.tabFocus < 0 && isTabPanel && isDropDownActive) {
                this.tabFocus = tabCount;
                focusOnDownDown();
              } else {
                const { isAllDisabled, newTabFocus } = this.handleTabFocus(liElements);
                this.tabFocus = newTabFocus;
                isAllTabsDisabled = isAllDisabled;
              }
              break;
            case Keys.home:
              this.tabFocus = 0;
              const { isAllDisabled, newTabFocus } = this.handleTabFocus(liElements, true);
              this.tabFocus = newTabFocus;
              isAllTabsDisabled = isAllDisabled;
              break;
            case Keys.end:
              this.tabFocus = tabCount - 1;
              const tabFocusInfo = this.handleTabFocus(liElements, true);
              this.tabFocus = tabFocusInfo.newTabFocus;
              isAllTabsDisabled = tabFocusInfo.isAllDisabled;
              break;
            default:
              break;
          }

          for (let item of liElements) {
            item.removeAttribute('tabindex');
          }

          if (!isAllTabsDisabled && !(isTabPanel && isDropDownActive && this.tabFocus === tabCount)) {
            liElements[this.tabFocus].setAttribute('tabindex', '-1');
            liElements[this.tabFocus].focus();
          }
        } else if ([Keys.enter, Keys.space].includes(e.keyCode)) {
          handleSelect({ selected: this.tabFocus });
        }
      }
    };

  /**
   * Returns which is the next focused tab, skipping disabled tabs, and whether all tabs are disabled
   * @param {HTMLCollection} liElements
   * @param {boolean} isIncrement
   */
  handleTabFocus = (liElements: HTMLCollection, isIncrement?: boolean): {isAllDisabled: boolean, newTabFocus: number } => {
    let { isTabPanel, tabCount } = this.props;

    if (isTabPanel) {
      tabCount--;
    }

    let newTabFocus = this.tabFocus;
    let isAllDisabled = true;

    for (let i = 0; i < tabCount; i++) {
      if (isIncrement && newTabFocus > tabCount - 1) {
        newTabFocus = 0;
      } else if (!isIncrement && newTabFocus < 0) {
        newTabFocus = tabCount - 1;
      }

      if (liElements[newTabFocus].getAttribute('class')?.search('k-disabled') !== -1) {
        newTabFocus = isIncrement ? newTabFocus + 1 : newTabFocus - 1;
      } else {
        isAllDisabled = false;
        break;
      }
    }

    return { isAllDisabled, newTabFocus: isAllDisabled ? 0 : newTabFocus };
  }

  /**
   * Clear tabIndex from LI elements
   * @param {KeyboardEvent} e
   */
  handleKeyDown = (e: KeyboardEvent): void => {
    const target = e.target as HTMLLIElement;
    const nodeType: string = _.get(target, 'nodeName').toLowerCase();

    if (('tab' === target?.getAttribute('role')) && (nodeType === 'li') && (Keys.tab === e.keyCode)) {
      let liElements = target.children;

      if (target?.getAttribute('role') === 'tab') {
        liElements = target?.parentElement.children;
      }

      for (let i = 0; i < liElements.length; i++) {
        liElements[i].removeAttribute('tabindex');
      }
    }
  }

  render() {
    const { className } = this.props;
    return (
      <div
        {...(className && {
          className: `${className} ${isFirefox() ? 'tp-firefox' : ''}`
        })}
        ref={this.reference}
        onKeyDown={this.handleKeyDown}
        onKeyUp={this.switchTab}
      >
        {this.props.children}
      </div>
    );
  }
}
